/**
 * The citation markers of a chat answer:
 * `[@shaarp/cite ids="w0123456789abcdef wfedcba9876543210"]`.
 *
 * The assistant writes the ids of the web sources it leans on, as the research
 * tools returned them — never a URL. The browser resolves each id against the
 * sources of the conversation, so a marker can only ever open a page a search
 * really returned: an id no source carries is dropped, not shown.
 *
 * This file is consumed by `@shaarp/ui`. `@shaarp/api` cannot import a runtime
 * value from this package (see `company-marker-quotes.ts`), so it keeps a
 * mirrored copy in `api/src/common/utils/citation-markers.ts`, held to this one
 * by `api/src/common/utils/citation-markers-parity.spec.ts`. Keep both in sync.
 */

/** What a source id looks like: `w` and the sixteen hex digits of its URL's hash. */
export const CITATION_SOURCE_ID_PATTERN = /^w[0-9a-f]{16}$/;

/** At most this many ids are read from one marker; the rest is noise. */
export const CITATION_MARKER_MAX_IDS = 8;

/**
 * The longest marker body read. Eight ids and their attribute fit in a fifth
 * of it; the bound is what keeps a scan linear on an answer that opens a
 * marker and never closes it — unbounded, every opening rescans to the end of
 * the text, and the text is written by a model that read scraped pages.
 */
const MARKER_BODY_MAX_CHARS = 400;

/**
 * The most blanks taken along before a marker. Bounded for the same reason as
 * the body: an unbounded run is rescanned from each of its positions, which is
 * quadratic on a long stretch of blanks with no marker after it.
 */
export const LEADING_BLANKS_MAX = 8;

/**
 * One complete marker, its body captured. A single `\s` after the tag, not
 * `\s+`: the body's class matches blanks too, and two quantifiers over the
 * same blanks backtrack quadratically on a marker left open.
 */
export const CITATION_MARKER_SOURCE = String.raw`\[@shaarp\/cite\s([^\]]{0,${MARKER_BODY_MAX_CHARS}})\]`;

/**
 * A marker still being written, at the very end of the text. Kept to its own
 * line: a marker the model never closed must not take the paragraphs after
 * it along.
 */
export const TRAILING_INCOMPLETE_CITATION_MARKER = new RegExp(
  String.raw`[ \t]{0,${LEADING_BLANKS_MAX}}\[@shaarp\/cite\b[^\]\n]{0,${MARKER_BODY_MAX_CHARS}}$`,
);

/**
 * The ids of one marker body, valid ones only, in order, without repeats. The
 * value may sit in straight, curly or French quotes, single ones or none, and
 * its hex in either case: the prompt is French, and a marker written « … », or
 * bare, must not lose its pill in silence. Neither widens what reaches a pill —
 * the body is bounded, and every id is still held to
 * {@link CITATION_SOURCE_ID_PATTERN} once lowercased.
 */
export function readCitationMarkerIds(attributes: string): string[] {
  const value = attributes.match(
    /\bids\s*=\s*["“”„‟«']?([^"“”„‟«»']*)["“”„‟»']?/,
  )?.[1];
  if (!value) return [];
  const ids = value
    .toLowerCase()
    .split(/[\s,;]+/)
    .filter((id) => CITATION_SOURCE_ID_PATTERN.test(id));
  return [...new Set(ids)].slice(0, CITATION_MARKER_MAX_IDS);
}

/** The ids each complete marker of `text` carries, one list per marker. */
export function parseCitationMarkers(text: string): string[][] {
  return [...text.matchAll(new RegExp(CITATION_MARKER_SOURCE, "g"))].map(
    (match) => readCitationMarkerIds(match[1] ?? ""),
  );
}

/**
 * Several markers in a row — a model writing one per source — read as one: a
 * sentence carries a single pill, whose list holds every source, up to the cap.
 */
export function mergeAdjacentCitationMarkers(text: string): string {
  const run = new RegExp(
    `${CITATION_MARKER_SOURCE}(?:[ \\t]*${CITATION_MARKER_SOURCE})+`,
    "g",
  );
  return text.replace(run, (chain) => {
    const ids = [...new Set(parseCitationMarkers(chain).flat())].slice(
      0,
      CITATION_MARKER_MAX_IDS,
    );
    return `[@shaarp/cite ids="${ids.join(" ")}"]`;
  });
}

/**
 * Every distinct id the answer's pills cite, in order of first citation: the
 * markers in a row merged and capped first, as the pills render them, so a
 * recap or a count never holds a source no pill opens.
 */
export function collectCitedSourceIds(text: string): string[] {
  return [
    ...new Set(parseCitationMarkers(mergeAdjacentCitationMarkers(text)).flat()),
  ];
}

/**
 * The text without its citation markers, for wherever the answer is read as
 * plain text — a copy, an email body, a question check. The space a marker
 * leaves before a punctuation mark or a line end goes with it, so no stray gap
 * is left.
 */
export function stripCitationMarkers(text: string): string {
  if (!text.includes("[@shaarp/cite")) return text;
  return mergeAdjacentCitationMarkers(text)
    .replace(TRAILING_INCOMPLETE_CITATION_MARKER, "")
    .replace(
      new RegExp(
        `[ \\t]{0,${LEADING_BLANKS_MAX}}${CITATION_MARKER_SOURCE}(?=[ \\t]*(?:[.,;:!?)]|\\n|$))`,
        "g",
      ),
      "",
    )
    .replace(new RegExp(`^${CITATION_MARKER_SOURCE}[ \\t]*`), "")
    .replace(
      new RegExp(
        `[ \\t]{0,${LEADING_BLANKS_MAX}}${CITATION_MARKER_SOURCE}[ \\t]*`,
        "g",
      ),
      " ",
    );
}
